<template>
  <k-modal :show="showModal">
    <template #header>
      <h2>Add Asset to the learner</h2>
    </template>
    <template #body>
      <div class="add-asset-form">
        <div class="form-group">
          <label>Asset Type</label>
          <k-dropdown
            v-model="selectedAssetType"
            :options="assetTypeOptions"
            placeholder="Select Asset Type"
            @update:modelValue="loadAssets"
          ></k-dropdown>
        </div>

        <div v-if="selectedAssetType && availableAssets.length > 0 && ready" class="assets-list">
          <h4>Available {{ selectedAssetTypeName }}</h4>
          <div class="assets-container">
            <k-table
              :headers="assetHeaders"
              :rows="tableRows"
              :max="10"
              :defaultHeaders="visibleHeaders"
              :hideable="true"
              :panel="false"
              @clicked="handleAssetAction">
            </k-table>
          </div>
        </div>

        <div v-else-if="selectedAssetType && ready && availableAssets.length === 0">
          <empty-placeholder
            title="No assets"
            info="No assets available for this type"
          ></empty-placeholder>
        </div>
      </div>
    </template>
    <template #footer>
      <button @click="$emit('close')" class="btn btn-outlined">Close</button>
    </template>
  </k-modal>
</template>

<style scoped>
.add-asset-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 500px;
  min-height: 300px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-group label {
  font-weight: 500;
  color: var(--kate-type-primary);
}

.assets-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.assets-list h4 {
  margin: 0;
  padding: 0;
  color: var(--kate-type-primary);
  font-size: 16px;
  font-weight: 600;
}

.assets-container {
  height: 350px;
  overflow-y: auto;
  border: 1px solid var(--kate-border);
  border-radius: 6px;
  background-color: var(--kate-background-body);
}

.assets-container .k-table-container {
  padding: 0 15px;
}
</style>

<script>
import KModal from '@base-components/k-modal.vue';
import KDropdown from '@base-components/k-dropdown.vue';
import KTable from '@base-components/k-table.vue';
import EmptyPlaceholder from '@base-components/empty-placeholder.vue';
import { ASSET_TYPES } from '../../../constants';

export default {
  components: {
    'k-modal': KModal,
    'k-dropdown': KDropdown,
    'k-table': KTable,
    'empty-placeholder': EmptyPlaceholder,
  },

  props: {
    userId: {
      type: Number,
      required: true,
    },
    moduleId: {
      type: Number,
      required: true,
    },
    showModal: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectedAssetType: '',
      availableAssets: [],
      isLoading: false,
    };
  },

  computed: {
    assetTypeOptions() {
      return Object.keys(ASSET_TYPES).map(key => ({
        id: key,
        name: ASSET_TYPES[key].prettyName,
      }));
    },
    ready() {
      return !this.isLoading;
    },
    selectedAssetTypeName() {
      if (!this.selectedAssetType) return '';
      return ASSET_TYPES[this.selectedAssetType]?.prettyName || '';
    },
    assetHeaders() {
      return {
        id: {
          name: 'ID',
          type: 'number',
          sortable: true,
        },
        name: {
          name: 'Name',
          type: 'string',
          sortable: true,
          filterable: true,
        },
        description: {
          name: 'Description',
          type: 'string',
        },
        displayTags: {
          name: 'Tags',
          type: 'tags',
          sortable: true,
          filterable: true,
        },
        actions: {
          name: 'Select Asset',
          type: 'action',
          sortable: false,
        },
      };
    },
    tableRows() {
      return this.availableAssets.map((asset, index) => ({
        id: asset.id,
        name: asset.name,
        description: asset.description || '',
        displayTags: { tags: asset.tags || [] },
        actions: {
          text: '<button class="btn btn-sm btn-primary">Add</button>',
          key: 'add',
          index,
        },
        index,
      }));
    },
    visibleHeaders() {
      return ['name', 'displayTags', 'actions'];
    },
  },

  methods: {
    loadAssets() {
      if (!this.selectedAssetType) return;
      this.isLoading = true;
      this.availableAssets = [];
      const apiRoot = ASSET_TYPES[this.selectedAssetType].apiRoot;
      this.$http.get(`/api/curriculum/admin/${apiRoot}`)
        .then(response => {
          const key = Object.keys(response.data)[0];
          this.availableAssets = response.data[key] || [];
        })
        .catch(err => {
          this.$ktoast.showError(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    handleAssetAction(key, index, row) {
      this.$logger.info('Adding a new asset to the learner');
      const payload = {
        module_id: this.moduleId,
        release_meta: {
          is_mandatory: true,
        },
      };
      this.$http.post(
        `/api/curriculum/admin/${this.selectedAssetType}/${row.id}/learner/${this.userId}`,
        payload,
      ).then(() => {
        this.$logger.info('Successfully added asset to the learner', { studentId: this.userId });
        this.$emit('refresh-modules');
      }).catch(err => {
        if (this.$http.isWarning(err)) {
          this.$logger.warn('There was an error while adding the asset', { studentId: this.userId, payload }, err);
        } else {
          this.$logger.error('There was an error while adding the asset', { studentId: this.userId, payload }, err);
        }
        this.$ktoast.showError(err);
      });
    },
  },
};
</script>
